import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../../../components/Page/PageHeader";

import useMessage from "../../../hooks/useMessage";
import SearchBar from "../../../components/Filtering/SearchBar";
import SnackBar from "../../../components/Alerts/SnackBar";
import { useGetModelFamiliesModelFamilyGet, useGetModelFamilyTagsModelFamilyTagsGet, useGetSupportedModelMethodsModelTrainSupportedModelMethodsGet } from "../../../api/generated/fastAPI";
import { GetModelFamiliesModelFamilyGetParams, ModelFamily, ModelFamilyListResponse } from "../../../api/models";
import { TableData, TableHeader } from "../../../components/Table/DataTable";
import { useNavigate } from "react-router-dom";
import Dropdown, { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import FilterBar, { FilterBarType } from "../../../components/Filtering/FilterBar";
import useDataParams, { GetParamsTypes } from "../../../hooks/useDataParams";
import TrainModelModal from "../components/TrainModelModal";
import BatchPredictionModal from "../../Resultsets/components/BatchPredictionModal";
import DeleteModal, { DeleteItemType } from "../../../components/Popups/DeleteModal";
import UpdatedPagination from "../../../components/Filtering/UpdatedPagination";
import SingleCompoundPredictionModal from "../../Resultsets/components/SingleCompoundPredictionModal";
import useDelete from "../../../hooks/endpoints/useDelete";
import AccessGate from "../../../components/Auth/AccessGate";
import { useAccess } from "../../../access/useAccess";
import { ac_access, demo_access } from "../../../access/predefinedConditions";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";
import useDownload from "../../../hooks/endpoints/useDownload";
import HierarchyTreeView from "../components/HierarchyTreeView";
import { ReactComponent as ModelIcon } from '../../../assets/Icons/navigation/Model.svg';
import { ReactComponent as ClearIcon } from '../../../assets/Icons/adornments/Bold-X.svg';
import { ReactComponent as NextIcon } from '../../../assets/Icons/chevron/Chevron-Right.svg';
import useModelFamilyTree from "../../../hooks/useModelFamilyTree";
import { customInstance } from "../../../api/custom-instance";
// import NewPageTabs from "../../../components/Tabs/NewPageTabs";

const modelFamilyHeaders: TableHeader[] = [
    {
        label: 'Name',
        value: 'name',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'Tags',
        value: 'tags',
        isShown: true,
        alignment: 'center',
    },
    // {
    //     label: 'Fingerprints',
    //     value: 'fingerprints',
    //     isShown: true,
    //     alignment: 'center',
    // },
    {
        label: 'Created By',
        value: 'created_by',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Owned By',
        value: 'owner',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Created',
        value: 'creation_date',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Updated',
        value: 'updated_date',
        isShown: true,
        alignment: 'center',
    },
];

const defaultParams = {
    name: null,
    created_by: null,
    owner: null,
    start_date: null,
    end_date: null,
    tag_groups: null,
    sort_by: 'creation_date',
    sort_order: 'desc',
    limit: 10,
    offset: 0,
    include_airflow_metadata: false,
    model_type: null,
    model_methods: null,
    tag_type_hierarchy: null,
    tag_hierarchy: null,
    grouped: true,
} as GetModelFamiliesModelFamilyGetParams;



// const tabs = ['All', 'Classification', 'Regression'];

export default function ModelFamilies(){
    const { canAccess } = useAccess();
    const hasAcAccess = canAccess({or: ac_access});

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const getModelFamilyTags = useGetModelFamilyTagsModelFamilyTagsGet();
    const getModelMethods = useGetSupportedModelMethodsModelTrainSupportedModelMethodsGet();

    const modelMethods = useMemo(() => {
        if (!getModelMethods.data) return [];

        const methods = getModelMethods.data as unknown as string[];
        return methods.sort();
    }, [getModelMethods.data]);

    const defaultFilters: FilterBarType[] = useMemo(() => {
        return [
            {
                label: 'Created Date Range',
                param: 'start_date',
                type: 'date-range',
                isDefault: true,
                groupKey: 'date-range',
            },
            {
                label: 'Created Date Range',
                param: 'end_date',
                type: 'date-range',
                isDefault: true,
                groupKey: 'date-range',
            },
            {
                label: 'Tags',
                param: 'tag_groups',
                type: 'tags',
                isDefault: true,
            },
            {
                label: 'Created By',
                param: 'created_by',
                type: 'users',
                isDefault: true,
            },
            {
                label: 'Owner',
                param: 'owner',
                type: 'users',
                isDefault: true,
            },
            {
                label: 'Model Methods',
                param: 'model_methods',
                type: 'multi-select',
                isDefault: true,
                icon: <ModelIcon />,
                options: modelMethods,
            }
        ];
    }, [modelMethods]);

    const {
        params,
        setParams,
        search,
        sort,
        filters,
        setPaginationLimit,
        setPaginationOffset,
    } = useDataParams({
        defaultParams,
        defaultFilters,
        tagsData: getModelFamilyTags.data as unknown as { [key: string]: string[] },
    })

    const getModelFamiliesMutation = useGetModelFamiliesModelFamilyGet(params as GetParamsTypes);

    const [ count, setCount ] = useState(0);
    const navigate = useNavigate();

    const [ modelFamilies, setModelFamilies ] = useState<TableData[]>([]); 
    const [ modelFams, setModelFams ] = useState<ModelFamily[]>([]);
    const [ selectedItems, setSelectedItems ] = useState<(string | number)[]>([]);
    const [ tableHeaders, setTableHeaders ] = useState<TableHeader[]>(modelFamilyHeaders);
    const [ actionDropdownOptions, setActionDropdownOptions ] = useState<DropdownOption[]>([]);

    const [ trainModelModalOpen, setTrainModelModalOpen ] = useState<boolean>(false);

    // Version expansion state
    const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());
    const [versionCache, setVersionCache] = useState<Record<string, ModelFamily[]>>({});
    const [loadingVersions, setLoadingVersions] = useState<Set<string | number>>(new Set());

    const isGrouped = !!(params as any).grouped;

    const toggleGrouped = () => {
        const newGrouped = !isGrouped;
        setExpandedRows(new Set());
        setVersionCache({});
        setSelectedItems([]);
        setParams(prev => ({ ...prev, grouped: newGrouped, offset: 0 }));
    };

    // Sync grouped param when access roles load
    useEffect(() => {
        setParams(prev => {
            if ((prev as any).grouped !== hasAcAccess) {
                return { ...prev, grouped: hasAcAccess };
            }
            return prev;
        });
    }, [hasAcAccess]);

    const refetchModelFamilies = () => {
        getModelFamiliesMutation.refetch();
    };

    const updateModelsByHierarchy = useCallback((
        tag_type_hierarchy: string[] | null,
        tag_hierarchy: string | null,
    ) => {
        setParams((prevParams) => ({
            ...prevParams,
            tag_type_hierarchy,
            tag_hierarchy,
        }))
    }, [setParams]);

    const {
        selectedCategories,
        isHierarchyDataLoading,
        isTreeLoading,
        activeId,
        filteredTreeItems,
        expandedItems,
        selectedTreeItem,
        handleDragStart,
        handleDragEnd,
        handleSelectCategory,
        handleAddCategory,
        handleRemoveCategory,
        selectedItemPath,
        handleSelectItemFromTree,
        handleSelectItemFromPath,
        handleSearch,
        handleExpandedItemsChange,
        handleExpandAll,
        handleCollapseAll,
        allItemIds,
        tagCategories,
    } = useModelFamilyTree({
        tag_groups: getModelFamilyTags.data as unknown as { [key: string]: string[] } | undefined,
        updateModelsByHierarchy,
    })

    const { deleteModelFamilies } = useDelete({ setPopupMessage, refetchFn: refetchModelFamilies });
    const { downloadModelFamilyStats } = useDownload();

    // Build model family fields (shared between group headers and version rows)
    const buildModelFamilyFields = useCallback((family: ModelFamily) => [
        {
            associatedColumn: 'name',
            value: family.name ? family.name : 'No value',
            alignment: 'left' as const,
            type: 'text' as const,
            action: (e:MouseEvent<HTMLButtonElement>) => navigate(`/models/${family.id!}`),
        },
        {
            associatedColumn: 'tags',
            value: (family.model_family_metadata?.tags ?? []) as string[],
            alignment: 'center' as const,
            type: 'tags' as const,
        },
        {
            associatedColumn: 'created_by',
            value: family.created_by ? family.created_by : 'No value',
            alignment: 'center' as const,
            type: 'text' as const,
        },
        {
            associatedColumn: 'owner',
            value: family.owner ? family.owner : 'No value',
            alignment: 'center' as const,
            type: 'text' as const,
        },
        {
            associatedColumn: 'creation_date',
            value: family.creation_date ? new Date(family.creation_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No value',
            alignment: 'center' as const,
            type: 'text' as const,
        },
        {
            associatedColumn: 'updated_date',
            value: family.updated_date ? new Date(family.updated_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No value',
            alignment: 'center' as const,
            type: 'text' as const,
        },
    ], [navigate]);

    useEffect(() => {
        if (getModelFamiliesMutation.data) {
            const response = getModelFamiliesMutation.data as unknown as ModelFamilyListResponse;
            const families = response.model_families ?? [];
            setCount(response.count ?? 0);
            setModelFams(families as unknown as ModelFamily[]);

            const items: TableData[] = families.map(family => {
                const groupCount = (family as any).group_count as number | undefined;
                return ({
                    id: family.id!,
                    fields: buildModelFamilyFields(family as unknown as ModelFamily),
                    options: injectOptions(family as unknown as ModelFamily),
                    isGroupHeader: hasAcAccess && (groupCount ?? 0) > 1,
                    groupCount: groupCount ?? undefined,
                });
            });

            setModelFamilies(items);
        }
        //eslint-disable-next-line
    }, [getModelFamiliesMutation.data]);

    // Flatten model families with expanded version rows
    const flattenedTableData = useMemo(() => {
        if (!isGrouped || expandedRows.size === 0) return modelFamilies;

        const items: TableData[] = [];
        for (const row of modelFamilies) {
            items.push(row);

            if (row.isGroupHeader && expandedRows.has(row.id)) {
                const mf = modelFams.find(f => f.id === row.id);
                if (!mf) continue;

                const cacheKey = `${mf.owner}::${mf.name}`;
                const versions = versionCache[cacheKey];

                if (versions) {
                    for (const v of versions) {
                        if (v.id === mf.id) continue;
                        items.push({
                            id: v.id!,
                            fields: buildModelFamilyFields(v),
                            options: injectOptions(v),
                            isVersionRow: true,
                        });
                    }
                } else if (loadingVersions.has(row.id)) {
                    items.push({
                        id: `loading-${row.id}`,
                        fields: [{
                            associatedColumn: 'name',
                            value: 'Loading versions...',
                            alignment: 'left' as const,
                            type: 'text' as const,
                        }],
                        isVersionRow: true,
                    });
                }
            }
        }
        return items;
    }, [modelFamilies, expandedRows, versionCache, loadingVersions, modelFams, isGrouped, buildModelFamilyFields]);

    // Handle expand/collapse of version groups
    const handleToggleExpand = useCallback(async (id: string | number) => {
        if (expandedRows.has(id)) {
            setExpandedRows(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            return;
        }

        // Expand
        setExpandedRows(prev => new Set(prev).add(id));

        const mf = modelFams.find(f => f.id === id);
        if (!mf) return;

        const cacheKey = `${mf.owner}::${mf.name}`;
        if (versionCache[cacheKey]) return;

        // Fetch versions
        setLoadingVersions(prev => new Set(prev).add(id));
        try {
            const response = await customInstance({
                url: '/model_family/versions',
                method: 'GET',
                params: {
                    owner: mf.owner,
                    name: mf.name,
                },
            }) as unknown as ModelFamilyListResponse;

            setVersionCache(prev => ({
                ...prev,
                [cacheKey]: (response.model_families ?? []) as unknown as ModelFamily[],
            }));
        } catch (err) {
            console.error('Failed to fetch model family versions:', err);
            setExpandedRows(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        } finally {
            setLoadingVersions(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    }, [expandedRows, modelFams, versionCache]);

    function injectOptions(modelfamily: ModelFamily) {
        const id = modelfamily.id!;
        const name = modelfamily.name!;

        let rowOptions = [
            {
                label: 'Run Batch Prediction',
                btnComp: (
                    <BatchPredictionModal
                        selectedModelFamilyIds={[Number(id)]}
                        setPopupMessage={setPopupMessage}
                        isPrimaryMenu={false}
                    />
                )
            },
            {
                label: 'Run Single Compound Prediction',
                btnComp: (
                    <SingleCompoundPredictionModal
                        selectedModelFamilyIds={[Number(id)]}
                        setPopupMessage={setPopupMessage}
                        location="dropdown"
                    />
                )
            },
            {
                label: 'Export Model Family Stats',
                action: async (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    let message = '';
                    let isSuccess = true;
                    try {
                        await downloadModelFamilyStats(
                            '.xlsx',
                            modelfamily,
                        );
                        message = `Model family stats downloaded successfully`;
                    } catch (err:any) {
                        console.log(err);
                        message = 'There was an error downloading this model family';
                        isSuccess = false;
                    }
                    setPopupMessage(message, isSuccess);
                },
             }, 
        ]
        if (canAccess({or: demo_access})) rowOptions.push(
            {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={[{
                            type: 'model',
                            id,
                            name,
                        }]}
                        deleteFn={deleteModelFamilies}
                    />
                )
            },
        );

        return rowOptions
    }

    useEffect(() => {
        let options: DropdownOption[] = [
            {
                label: 'Run Batch Prediction',
                btnComp: (
                    <BatchPredictionModal
                        selectedModelFamilyIds={selectedItems.map(item => Number(item))}
                        setPopupMessage={setPopupMessage}
                        isPrimaryMenu={false}
                        cancelFn={() => setSelectedItems([])}
                    />
                )
            },
            {
                label: 'Run Single Compound Prediction',
                btnComp: (
                    <SingleCompoundPredictionModal
                        selectedModelFamilyIds={selectedItems.map(item => Number(item))}
                        setPopupMessage={setPopupMessage}
                        location="dropdown"
                        cancelFn={() => setSelectedItems([])}
                    />
                )
            },
        ];

        if (modelFams.length > 0 && selectedItems.length > 0) {
            let items: DeleteItemType[] = [];
            const exportItems: ModelFamily[] = [];
            selectedItems.forEach(id => {
                let matchingItem = modelFams.find(fam => fam.id === id);
                // Also check version cache for expanded version rows
                if (!matchingItem) {
                    for (const versions of Object.values(versionCache)) {
                        const found = versions.find(v => v.id === id);
                        if (found) { matchingItem = found; break; }
                    }
                }
                if (matchingItem) {
                    items.push({
                        type: 'model',
                        id,
                        name: matchingItem.name ? matchingItem.name : ''
                    });

                    exportItems.push(matchingItem);
                }
            });

            options.push({
                label: 'Export Model Family Stats',
                action: async (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    let message = '';
                    let isSuccess = true;

                    const promises = exportItems.map(async (item) => {
                        return downloadModelFamilyStats(
                            '.xlsx',
                            item,
                        );
                    })
                    try {
                        await Promise.all(promises);
                        setSelectedItems([]);
                        message = `Model family stats downloaded successfully`;
                    } catch (err:any) {
                        console.log(err);
                        message = 'There was an error downloading this model family';
                        isSuccess = false;
                    }
                    setPopupMessage(message, isSuccess);
                },
            })

            const deleteModalComp: DropdownOption = {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={items}
                        cancelFn={() => setSelectedItems([])}
                        deleteFn={deleteModelFamilies}
                    />
                ),
            };

            if (canAccess({or: demo_access})) options.push(deleteModalComp);
        };

        setActionDropdownOptions(options);

        //eslint-disable-next-line
    }, [selectedItems, modelFams, versionCache]);

    // const handleTabChange = (event: React.SyntheticEvent, index: number) => {
    //     setSelectedIndex(index);

    //     const selectedTab = tabs[index];

    //     setParams((prevParams) => ({
    //         ...prevParams,
    //         model_type: selectedTab === 'Classification' ? 'classification' : selectedTab === 'Regression' ? 'regression' : null,
    //     }));
        
    // }
    

    return (
        <div className="h-screen overflow-hidden flex justify-between items-stretch">
            <HierarchyTreeView 
                isLoading={getModelFamilyTags.isLoading}
                isHierarchyDataLoading={isHierarchyDataLoading || isTreeLoading}
                selectedCategories={selectedCategories}
                activeId={activeId}
                filteredTreeItems={filteredTreeItems}
                expandedItems={expandedItems}
                selectedTreeItem={selectedTreeItem}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
                handleSelectCategory={handleSelectCategory}
                handleAddCategory={handleAddCategory}
                handleRemoveCategory={handleRemoveCategory}
                handleSelectItemFromTree={handleSelectItemFromTree}
                handleSearch={handleSearch}
                handleExpandedItemsChange={handleExpandedItemsChange}
                handleExpandAll={handleExpandAll}
                handleCollapseAll={handleCollapseAll}
                allItemIds={allItemIds}
                tagCategories={tagCategories}
            />
            <div className="grow flex flex-col justify-start items-stretch gap-4 p-10">

                <div className="w-full flex flex-col justify-start items-start mb-4">

                <PageHeader label='Model Families'>
                    <AccessGate
                        or={ac_access}
                    >
                        <TrainModelModal 
                            setPopupMessage={setPopupMessage}
                            isDropdownItem={false}
                            open={trainModelModalOpen}
                            setOpen={setTrainModelModalOpen}
                        />
                    </AccessGate>
                </PageHeader>

                {selectedItemPath.length > 0 && (
                    <div className="w-full flex flex-wrap justify-start items-center gap-5">
                        <div className="flex flex-wrap justify-start items-center gap-2">
                            {selectedItemPath.map((path, i) => (
                                <>
                                    <button 
                                        onClick={(e:any) => handleSelectItemFromPath(e, path.id)}
                                        disabled={path.id === selectedTreeItem} 
                                        className='text-default hover:text-primary hover:disabled:text-default transition-all flex justify-start items-center gap-2'
                                    >
                                        <div style={{ backgroundColor: path.color }} className="w-[10px] h-[10px] rounded-full"/>
                                        <p className="text-[14px] font-medium">{path.label}</p>
                                    </button>
                                    {i < selectedItemPath.length-1 && (
                                        <div className="w-[16px] text-default"><NextIcon /></div>
                                    )}
                                </>
                            ))}
                        </div>
                        <button onClick={(e:any) => handleSelectItemFromPath(e, null)} className="text-disable hover:text-primary transition-all flex justify-center items-center gap-1">
                            <div className="w-[16px]"><ClearIcon /></div>
                            <p className="text-[14px] font-medium">Clear all</p>  
                        </button>
                    </div>
                )}
                </div>

                <div className="flex flex-col justify-start items-start gap-2">
                    <div className="w-full flex justify-between items-center gap-4">
                        <div className="max-w-[600px] w-full">
                            <SearchBar
                                searchFn={search}
                                searchKey="name"
                            />
                        </div>
                        <div className="flex justify-end items-center gap-4">
                            {hasAcAccess && (
                                <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm">
                                    <button
                                        className={`px-3 py-2.5 transition-colors ${isGrouped ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                        onClick={toggleGrouped}
                                        disabled={isGrouped}
                                    >
                                        Grouped
                                    </button>
                                    <button
                                        className={`px-3 py-2.5 transition-colors ${!isGrouped ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                        onClick={toggleGrouped}
                                        disabled={!isGrouped}
                                    >
                                        Flat
                                    </button>
                                </div>
                            )}
                            <ColumnsButton 
                                headers={tableHeaders} 
                                setHeaders={setTableHeaders} 
                                isDisabled={false}
                            />
                            {selectedItems.length > 0 && (
                                <Dropdown 
                                    btn={{
                                        label: `Actions (${selectedItems.length} selected)`,
                                        type: 'secondary',
                                        styling: 'solid',
                                        id: 'model-family-multi-action-btn',
                                    }}
                                    options={actionDropdownOptions}
                                />
                            )}
                        </div>
                    </div>
                    <FilterBar 
                        filters={filters}
                        setParams={setParams}
                        params={params}
                    />
                </div>

                <VirtualizedDataTable 
                    paramValues={Object.entries(params).filter(entry => ['name', 'start_date', 'end_date'].includes(entry[0]) && entry[1]).map(val => val[1])}
                    isLoading={getModelFamiliesMutation.isLoading} 
                    headers={tableHeaders} 
                    tableData={flattenedTableData}
                    selection={{
                        selectedItems,
                        setSelectedItems
                    }}
                    sorting={{
                        sortableHeaders: ['name', 'created_by', 'owner', 'creation_date', 'updated_date'],
                        sortHeaders: sort,
                        sortByParam: params.sort_by!,
                        sortOrderParam: params.sort_order!,
                    }}
                    setPopupMessage={setPopupMessage}
                    fixedItemHeight={72}
                    count={count}
                    dataType="model-families"
                    expandable={isGrouped}
                    expandedRows={expandedRows}
                    onToggleExpand={handleToggleExpand}
                />

                <UpdatedPagination 
                    count={count}
                    limit={params.limit!}
                    offset={params.offset!}
                    setPaginationLimit={setPaginationLimit}
                    setPaginationOffset={setPaginationOffset}
                    size="reg"
                    isLoading={getModelFamiliesMutation.isLoading}
                />
                
                <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  

        
            </div>
        </div>
    )
}